interface FormatOptions {
  /** The text content that exists before the insertion point */
  textBefore?: string;
  /** Whether this is the first insertion in the document */
  isFirstInsertion?: boolean;
}

/**
 * Formats raw speech recognition text with proper punctuation spacing and capitalization
 * Handles all text formatting logic including spacing between segments
 */
export function formatSpeechText(
  text: string,
  options: FormatOptions = {},
): string {
  if (!text) {
    return '';
  }

  const { textBefore = '', isFirstInsertion = false } = options;

  let formatted = text.trim();

  // Step 1: Clean up spacing around punctuation
  formatted = formatted
    // Add space after sentence-ending punctuation if followed by letter
    .replace(/([.!?])([A-Za-zÄÖÜäöüß])/g, '$1 $2')
    // Add space after comma, semicolon, colon if followed by letter
    .replace(/([,;:])([A-Za-zÄÖÜäöüß])/g, '$1 $2')
    // Remove duplicate spaces
    .replace(/\s+/g, ' ')
    // Remove space before punctuation
    .replace(/\s+([.!?,;:])/g, '$1');

  // Step 2: Handle capitalization
  // Check if we should capitalize the first letter
  const shouldCapitalizeFirst =
    isFirstInsertion || !textBefore || /[.!?]\s*$/.test(textBefore.trim());

  if (shouldCapitalizeFirst) {
    // Capitalize first letter
    formatted = formatted.replace(/^([a-zäöüß])/, (match) =>
      match.toUpperCase(),
    );
  }

  // Capitalize after sentence-ending punctuation within the text
  formatted = formatted.replace(
    /([.!?]\s+)([a-zäöüß])/g,
    (_match, p1, p2) => p1 + p2.toUpperCase(),
  );

  // Step 3: Handle spacing between existing text and new text
  if (textBefore && textBefore.length > 0) {
    const trimmedTextBefore = textBefore.trimEnd();
    if (trimmedTextBefore.length > 0) {
      const lastChar = trimmedTextBefore[trimmedTextBefore.length - 1];

      // Check if we need to add a space before the new text
      const needsSpace =
        // Always add space after sentence-ending punctuation
        ['.', '!', '?'].includes(lastChar) ||
        // Add space after other characters unless they're special cases
        (lastChar &&
          lastChar !== ' ' &&
          // Don't add space after opening punctuation
          !['(', '[', '{', '"', "'", '`'].includes(lastChar) &&
          // Don't add space if new text starts with closing punctuation
          !/^[)\]},;:.!?]/.test(formatted));

      if (needsSpace) {
        formatted = ' ' + formatted;
      }
    }
  }

  return formatted;
}

/**
 * Helper function to determine if a character is sentence-ending punctuation
 */
export function isSentenceEnd(char: string): boolean {
  return ['.', '!', '?'].includes(char);
}

/**
 * Helper function to extract the last character before a position in text
 */
export function getLastCharBeforePosition(
  text: string,
  position: number,
): string {
  if (position <= 0 || position > text.length) {
    return '';
  }

  return text[position - 1] || '';
}
